/**
 * GUPM-329 — rótulo de exibição de uma estrutura, a partir do código.
 *
 * Existe porque `dams` deixou de conter só barragens. A Mina entrou como quarta linha
 * (o marco superficial precisa de `dam_id`, que é NOT NULL), e a UI montava o rótulo
 * como `Barragem ${code}` em cerca de vinte pontos — fazendo a Mina aparecer como
 * **"Barragem MINA"** no chip do header, no seletor de abertura de TARP e no modal de
 * Plano de Ação. A migration que a cria declara `classificacao_dpa = 'nao_aplicavel'`:
 * ela não é barragem regulada pela ANM Res. 95/2022.
 *
 * Domínio puro: sem React, sem Supabase, sem DOM.
 */

/**
 * Estruturas que não são barragem, e como se chamam. Mapa explícito em vez de
 * heurística: quando a próxima estrutura entrar (uma pilha, um dique), ela aparece
 * aqui — e, até aparecer, cai no ramo que devolve o código puro em vez de herdar
 * "Barragem" por omissão, que foi exatamente como a Mina virou "Barragem MINA".
 */
const NAO_SAO_BARRAGEM: Readonly<Record<string, string>> = {
  // "Mina", e não "Mina UPM" (decisão do dono, 11/09/2026): o app inteiro é da UPM,
  // então a unidade não distingue esta estrutura das outras três — só ocupa largura
  // no chip do header, que ela divide com A, B e C. O cadastro segue com o nome
  // completo em `dams.name`; o rótulo de tela é montado a partir do CÓDIGO.
  MINA: 'Mina',
};

/** Códigos que são barragem de fato, sob ANM Res. 95/2022. */
const BARRAGENS: readonly string[] = ['A', 'B', 'C'];

/**
 * Rótulo de exibição da estrutura. `null` quando não há código — nunca uma string
 * inventada, que apareceria na tela como se fosse nome de estrutura.
 *
 * O formato "Barragem A/B/C" vem do protótipo V8 e é montado a partir do CÓDIGO, e
 * não de `dams.name`: o cadastro traz sufixos ("Barragem B — UPM (prioridade)") que
 * não cabem num chip. Ver GUPM-199/206.
 */
export function rotuloDaEstrutura(code: string | null | undefined): string | null {
  if (!code) return null;
  const limpo = code.trim();
  if (limpo === '') return null;

  const maiusculo = limpo.toUpperCase();
  const proprio = NAO_SAO_BARRAGEM[maiusculo];
  if (proprio) return proprio;

  if (BARRAGENS.includes(maiusculo)) return `Barragem ${maiusculo}`;

  // Estrutura que ainda não tem regra: devolve o que se sabe, sem afirmar categoria.
  return limpo;
}

/**
 * Forma CURTA do rótulo — para onde o espaço é estreito, como o chip do header,
 * que divide uma barra com todas as estruturas.
 *
 * Da barragem devolve só a letra ("A"), que é como o V8 desenhou o chip. Aplicar a
 * mesma regra à Mina exibia o código cru, "MINA" em caixa alta ao lado de "A", "B" e
 * "C" — daí o ramo próprio: estrutura que não é barragem usa o nome dela.
 */
export function rotuloCurtoDaEstrutura(code: string | null | undefined): string | null {
  const cheio = rotuloDaEstrutura(code);
  if (cheio === null) return null;
  return ehBarragem(code) ? code!.trim().toUpperCase() : cheio;
}

/** `true` quando a estrutura é barragem regulada pela ANM (exclui a Mina). */
export function ehBarragem(code: string | null | undefined): boolean {
  if (!code) return false;
  return BARRAGENS.includes(code.trim().toUpperCase());
}
